<script setup>
import { ref, computed } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import LayoutToggle from '@/Components/UI/LayoutToggle.vue'
import Icon from '@/Components/UI/Icon.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import VehicleCard from '@/Components/Cards/VehicleCard.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { useLayoutMode } from '@/composables/useLayoutMode.js'
import { setPageHeaderCount } from '@/composables/usePageHeader.js'

const LAYOUT_MODES = [
  { v: 'cards',   label: 'Cards',   icon: 'layout-cards' },
  { v: 'table',   label: 'Table',   icon: 'layout-table' },
  { v: 'compact', label: 'Compact', icon: 'layout-list'  },
]

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatMoney } = useCurrency()

const props = defineProps({
  vehicles:      Array,
  meta:          Object,
  filters:       Object,
  status_counts: Object,
  categories:    { type: Array, default: () => [] },
  fuel_types:    { type: Array, default: () => [] },
})

const page    = usePage()
const canAct  = computed(() => page.props.auth?.user?.role !== 'viewer')

const search       = ref(props.filters?.q || '')
const statusFilter = ref(props.filters?.status || 'all')
const layoutMode   = useLayoutMode('vehicles', LAYOUT_MODES, 'cards')

let searchTimer = null
const onSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => applyFilters(), 200)
}

const applyFilters = () => {
  router.get('/vehicles', {
    status:    statusFilter.value === 'all' ? '' : statusFilter.value,
    q:         search.value,
    category:  props.filters?.category || null,
    fuel_type: props.filters?.fuel_type || null,
    ownership: props.filters?.ownership || null,
  }, { preserveState: true })
}

const updateCategory = (v) => {
  router.get('/vehicles', {
    status:    statusFilter.value === 'all' ? '' : statusFilter.value,
    q:         search.value,
    category:  v || null,
    fuel_type: props.filters?.fuel_type || null,
    ownership: props.filters?.ownership || null,
    page:      1,
  }, { preserveState: true, preserveScroll: true })
}

const updateFuelType = (v) => {
  router.get('/vehicles', {
    status:    statusFilter.value === 'all' ? '' : statusFilter.value,
    q:         search.value,
    category:  props.filters?.category || null,
    fuel_type: v || null,
    ownership: props.filters?.ownership || null,
    page:      1,
  }, { preserveState: true, preserveScroll: true })
}

setPageHeaderCount(computed(() => props.meta?.total ?? 0), (n) => t('vehicles.count', { n }).replace(/^\d+\s*/, ''))

const statusRibbonClass = (status) => ({
  available:   'bg-green-600',
  rented:      'bg-blue-600',
  maintenance: 'bg-amber-600',
  retired:     'bg-gray-400',
}[status] || 'bg-gray-400')

const statuses = ['all', 'available', 'rented', 'maintenance', 'retired']
const statusOptions = computed(() => statuses.map((s) => ({
  value: s,
  label: t(`vehicles.status.${s}`),
  count: props.status_counts?.[s] ?? 0,
})))

const setStatus = (s) => {
  statusFilter.value = s
  applyFilters()
}

const ownershipOptions = computed(() => [
  { value: 'all',        label: t('vehicles.ownership.all') },
  { value: 'owned',      label: t('vehicles.ownership.owned') },
  { value: 'sub_rented', label: t('vehicles.ownership.sub_rented') },
])

const categoryOptions = computed(() => [
  { value: '', label: t('vehicles.filters.allCategories') },
  ...(props.categories || []).map((c) => ({
    value: c,
    label: t(`vehicles.categories.${c}`, c.charAt(0).toUpperCase() + c.slice(1)),
  })),
])

const fuelTypeOptions = computed(() => [
  { value: '', label: t('vehicles.filters.allFuelTypes') },
  ...(props.fuel_types || []).map((f) => ({
    value: f,
    label: t(`vehicles.fuelTypes.${f}`, f.charAt(0).toUpperCase() + f.slice(1)),
  })),
])

const updateOwnership = (v) => {
  router.get('/vehicles', {
    status:    statusFilter.value === 'all' ? '' : statusFilter.value,
    q:         search.value,
    ownership: v === 'all' ? null : v,
    page:      1
  }, { preserveState: true, preserveScroll: true })
}
</script>

<template>
  <PageShell
    :back-href="filters?.from === 'dashboard' ? '/' : ''"
    :back-label="t('common.backToDashboard', 'Dashboard')"
    :title="t('vehicles.title', 'Fleet')"
  >
    <template #actions>
      <Link
        v-if="canAct"
        href="/vehicles/new"
      >
        <UiButton
          variant="primary"
          icon-left="plus"
        >
          {{ t('vehicles.new') }}
        </UiButton>
      </Link>
    </template>

    <template #filters>
      <UiInput
        v-model="search"
        type="search"
        icon-left="search"
        :placeholder="t('vehicles.searchPlaceholder')"
        class="flex-1 min-w-[12rem] max-w-sm"
        @update:model-value="onSearch"
      />
      <UiSegmented
        :model-value="statusFilter"
        :options="statusOptions"
        @update:model-value="setStatus"
      />
      <UiSegmented
        :model-value="filters?.ownership || 'all'"
        :options="ownershipOptions"
        @update:model-value="updateOwnership"
      />
      <div class="w-32">
        <UiSelect
          :model-value="filters?.category || ''"
          :options="categoryOptions"
          @update:model-value="updateCategory"
        />
      </div>
      <div class="w-32">
        <UiSelect
          :model-value="filters?.fuel_type || ''"
          :options="fuelTypeOptions"
          @update:model-value="updateFuelType"
        />
      </div>
      <div class="ml-auto">
        <LayoutToggle
          v-model="layoutMode"
          :modes="LAYOUT_MODES"
        />
      </div>
    </template>

    <!-- Grid -->
    <div
      v-if="layoutMode === 'cards' && vehicles?.length"
      class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4"
    >
      <div
        v-for="v in vehicles"
        :key="v.id"
        class="rounded-xl border border-[color:var(--line)] overflow-hidden hover:shadow-md transition-shadow group"
        :style="{ background: 'var(--surface)' }"
      >
        <VehicleCard :vehicle="v">
          <template #actions>
            <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Link
                :href="`/vehicles/${v.id}`"
                class="px-3 py-1.5 text-xs font-medium rounded-lg bg-[color:var(--surface)] text-[color:var(--ink)] hover:bg-[color:var(--surfaceAlt)]"
              >
                {{ t('common.view') }}
              </Link>
              <Link
                v-if="canAct"
                :href="`/vehicles/${v.id}/edit`"
              >
                <UiButton
                  variant="primary"
                  size="sm"
                >
                  {{ t('common.edit') }}
                </UiButton>
              </Link>
            </div>
          </template>
        </VehicleCard>
      </div>
    </div>

    <!-- Table view -->
    <div v-if="layoutMode === 'table' && vehicles?.length">
      <!-- Mobile: card stack -->
      <div
        class="md:hidden grid gap-3"
        style="grid-template-columns: 1fr;"
      >
        <Link
          v-for="v in vehicles"
          :key="v.id"
          :href="`/vehicles/${v.id}`"
          class="block rounded-xl overflow-hidden border border-[color:var(--line)]"
          :style="{ background: 'var(--surface)' }"
        >
          <VehicleCard :vehicle="v" />
        </Link>
      </div>

      <!-- md+: existing table (verbatim, just wrapped) -->
      <div
        class="hidden md:block rounded-xl overflow-hidden"
        :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
      >
        <table
          class="w-full text-sm"
          role="grid"
        >
          <thead :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
            <tr>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]"
              >
                Vehicle
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]"
              >
                Reg
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]"
              >
                Status
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]"
              >
                Daily rate
              </th>
              <th
                scope="col"
                class="px-4 py-3 w-10"
              />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="v in vehicles"
              :key="v.id"
              class="transition-colors hover:bg-[var(--surfaceAlt)]"
              :style="{ borderTop: '1px solid var(--line)' }"
            >
              <td class="px-4 py-3">
                <Link
                  :href="`/vehicles/${v.id}`"
                  class="font-medium"
                  :style="{ color: 'var(--ink)' }"
                >
                  {{ v.make }} {{ v.model }}
                </Link>
                <span
                  v-if="v.currently_sub_rented"
                  class="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                  :style="{ background: 'color-mix(in oklab, var(--warn) 18%, transparent)', color: 'var(--warn)' }"
                >
                  {{ t('vehicles.subRentedBadge') }}
                </span>
              </td>
              <td
                class="px-4 py-3 mono text-xs"
                style="color: var(--muted);"
              >
                {{ v.registration }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full capitalize text-white"
                  :class="statusRibbonClass(v.status)"
                >
                  {{ t(`vehicles.status.${v.status}`) }}
                </span>
              </td>
              <td
                class="px-4 py-3 text-right mono text-sm fw-value tabular-nums"
                style="color: var(--ink);"
              >
                {{ formatMoney(v.daily_rate_cents) }}/d
              </td>
              <td class="px-4 py-3">
                <Link
                  :href="`/vehicles/${v.id}`"
                  class="text-xs font-medium"
                  :style="{ color: 'var(--accent)' }"
                >
                  {{ t('common.view') }}
                </Link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Compact view -->
    <div
      v-if="layoutMode === 'compact' && vehicles?.length"
      class="rounded-xl overflow-hidden"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <ul role="list">
        <li
          v-for="(v, i) in vehicles"
          :key="v.id"
          :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
        >
          <Link
            :href="`/vehicles/${v.id}`"
            class="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-[var(--surfaceAlt)]"
          >
            <span
              class="w-2 h-2 rounded-full flex-shrink-0"
              :class="statusRibbonClass(v.status)"
            />
            <span
              class="text-[0.8125rem] font-medium truncate flex-1"
              style="color: var(--ink);"
            >{{ v.make }} {{ v.model }}</span>
            <span
              v-if="v.currently_sub_rented"
              class="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
              :style="{ background: 'color-mix(in oklab, var(--warn) 18%, transparent)', color: 'var(--warn)' }"
            >{{ t('vehicles.subRentedBadge') }}</span>
            <span
              class="mono text-[0.6875rem]"
              style="color: var(--muted);"
            >{{ v.registration }}</span>
            <span
              class="mono text-[0.75rem] tabular-nums"
              style="color: var(--inkSoft);"
            >{{ formatMoney(v.daily_rate_cents) }}/d</span>
            <Icon
              name="chev-r"
              :size="14"
              class="opacity-40"
            />
          </Link>
        </li>
      </ul>
    </div>

    <EmptyState
      v-if="!vehicles?.length"
      :type="search || statusFilter !== 'all' ? 'filter' : 'first-use'"
      :title="search ? t('vehicles.emptySearch', { q: search }) : t('vehicles.emptyFirst')"
      :message="search ? t('vehicles.emptySearchHint') : t('vehicles.emptyFirstHint')"
      @clear="search = ''; statusFilter = 'all'; applyFilters()"
    />
  </PageShell>
</template>
